import {
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ISidebarRailProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useSidebarRootContext,
    useSidebarContext
} from "../../context";

const SidebarRail: FC<ISidebarRailProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    ...props
}) => {
    const provider = useSidebarContext({
        part: "Sidebar.Rail"
    });

    const root = useSidebarRootContext({
        part: "Sidebar.Rail"
    });

    const customLocalize = customLocalizeProp ?? root?.customLocalize;

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        shapeConfigs,
        touchTargets,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isCollapsed = root?.isCollapsed ?? false;

    const {
        container: containerDynamicStyle,
        line: lineDynamicStyle
    } = useStyles({
        isActive: isHovered || isFocusVisible,
        side: root?.side ?? "left",
        shapeConfigs,
        touchTargets,
        isCollapsed,
        direction,
        isPressed,
        borders,
        colors
    });

    if(Platform.OS !== "web" || root?.isPhone || root?.collapseBehaviour === "none") {
        return null;
    }

    return <Pressable
        {...props}
        {...interactionHandlers}
        aria-label={title ?? localize(isCollapsed ? "expand-sidebar" : "collapse-sidebar")}
        aria-controls={provider?.sidebarId}
        onPress={(event) => {
            onPress?.(event);

            provider?.handleToggle();
        }}
        aria-expanded={!isCollapsed}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="button"
        tabIndex={-1}
    >
        <View
            style={[
                stylesheet.line,
                lineDynamicStyle
            ]}
        />
    </Pressable>;
};
export default SidebarRail;
